<template>
  <ion-app>
    <!-- Network error: system settings failed to load -->
    <base-network-error v-if="hasNetworkError" @retry="retryStartup" />
    <!-- Show pulsing logo loader while validating tokens on startup -->
    <base-full-page-loader v-else-if="isValidatingTokens" />
    <!-- Only show app content after token validation completes -->
    <ion-router-outlet v-else />
  </ion-app>
</template>

<script setup>
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { onMounted, onUnmounted, ref } from 'vue';
import { Network } from '@capacitor/network';
import BaseFullPageLoader from '@/components/Base/BaseFullPageLoader.vue';
import BaseNetworkError from '@/components/Base/BaseNetworkError.vue';
import { initializeSafeArea } from '@/composables/useSafeArea';
import { initializeAdMob, setupAdMobListeners } from '@/composables/useAdMob';
import { useSubscription } from '@/composables/useSubscription';
import { usePushNotifications } from '@/composables/usePushNotifications';
import { useSystemStore } from '@/stores/system';
import { useUserStore } from '@/stores/user';
import { useOnboardingStore } from '@/stores/onboarding';
import { tokenStorage } from '@/services/tokenStorage';
import { useRouter } from 'vue-router';
import { useDeepLinks } from '@/composables/useDeepLinks';
import { configureRevenueCat, setSubscriberAttributes } from '@/services/revenueCat';
import { useCustomerIo } from '@/composables/useCustomerIo';
import { Sentry } from '@/services/sentry';
import { syncSystemLocale, initLanguageSyncListener, removeLanguageSyncListener } from '@/composables/useLanguageSettings';

const router = useRouter();
const { init: initDeepLinks, handleDeepLink, processPendingPremium } = useDeepLinks();
const { checkPremiumAccess } = useSubscription();
const pushNotifications = usePushNotifications();
const { init: initCustomerIo } = useCustomerIo();

// Loading state to prevent race conditions during token validation
const isValidatingTokens = ref(true);
const hasNetworkError = ref(false);

let networkListenerHandle = null;

const continueStartup = async () => {
  const accessToken = await tokenStorage.getAccessToken();

  // Fetch user settings if authenticated
  if (accessToken && accessToken.length > 0) {
    const userStore = useUserStore();
    const onboardingStore = useOnboardingStore();

    try {
      console.log('👤 Fetching user data on app startup...');

      await userStore.fetchUserData();

      // Sync system language to app (for per-app language settings on iOS/Android 13+)
      await syncSystemLocale();

      isValidatingTokens.value = false;

      // Configure RevenueCat with the user ID for returning users
      if (userStore.settings.id) {
        try {
          await configureRevenueCat(userStore.settings.id);

          // Set subscriber attributes (email, username, etc.)
          await setSubscriberAttributes({
            email: userStore.settings.email,
            displayName: userStore.settings.username,
          });

          // Check premium status and update store
          await checkPremiumAccess();

          // Process any premium deep link that was deferred during cold boot
          await processPendingPremium();

          console.log('✅ Premium status checked on startup');
        } catch (error) {
          console.error('❌ Failed to configure RevenueCat on startup:', error);
          Sentry.captureException(error);
          // Non-blocking - app can continue without RevenueCat
        }
      }

      // Register for push notifications if user is authenticated and permission is granted
      // This happens AFTER authentication to ensure we can send token to backend
      await pushNotifications.initializeForAuthenticatedUser(onboardingStore.isCompleted);

      // Redirect to onboarding if user hasn't completed it yet
      // This handles the case where a user has tokens but didn't complete onboarding
      // (e.g., closed app during onboarding and came back later)
      const currentPath = router.currentRoute.value.path;

      if (!onboardingStore.isCompleted && !currentPath.startsWith('/onboarding') && !currentPath.startsWith('/auth')) {
        console.log('🎯 User has not completed onboarding, redirecting from', currentPath, 'to /onboarding');

        await router.replace('/onboarding');
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('❌ Failed to load user data on startup:', error);
        Sentry.captureException(error);
      }

      // If it's a 401, redirect to login page
      if (error.response?.status === 401) {
        isValidatingTokens.value = false;

        await router.replace('/auth');
      }
      // Non-blocking for other errors
    }
  } else {
    isValidatingTokens.value = false;
  }
};

const fetchSystemSettingsAndContinue = async () => {
  const systemStore = useSystemStore();

  try {
    await systemStore.fetchSystemSettings();

    hasNetworkError.value = false;

    await continueStartup();
  } catch (error) {
    console.error('Failed to load system settings on startup:', error);
    Sentry.captureException(error);

    hasNetworkError.value = true;
    isValidatingTokens.value = false;

    await setupNetworkListener();
  }
};

const retryStartup = async () => {
  hasNetworkError.value = false;
  isValidatingTokens.value = true;

  await fetchSystemSettingsAndContinue();
};

const setupNetworkListener = async () => {
  // Avoid duplicate listeners
  if (networkListenerHandle) {
    return;
  }

  networkListenerHandle = await Network.addListener('networkStatusChange', async (status) => {
    console.log('🌐 Network status changed:', status);

    if (status.connected && hasNetworkError.value) {
      console.log('🔄 Network restored, retrying system settings...');

      await removeNetworkListener();
      await retryStartup();
    }
  });
};

const removeNetworkListener = async () => {
  if (networkListenerHandle) {
    await networkListenerHandle.remove();

    networkListenerHandle = null;
  }
};

onMounted(async () => {
  // Set up push notification listeners FIRST (before any registration can happen)
  // This ensures we catch the 'registration' event when iOS/Android returns the device token
  pushNotifications.setupListeners({
    onNotificationTap: (link) => handleDeepLink(link),
  });
  pushNotifications.setupAppStateListener();

  // Initialize deep links
  initDeepLinks();

  // Initialize safe area insets
  initializeSafeArea();

  // Initialize AdMob
  await initializeAdMob();
  setupAdMobListeners();

  // Initialize Customer.io
  initCustomerIo('14948f153bbea22d971f');

  // Initialize language sync listener for app resume events
  // This syncs system language when user returns from system settings
  initLanguageSyncListener();

  console.log('🚀 App.vue mounted - Starting token validation...');

  // Monitor localStorage changes (for web platform debugging)
  // This helps detect if tokens are being cleared by browser or other tabs
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      // If tokens were cleared, log it prominently
      if (e.key === 'capacitor-storage_access_token' || e.key === 'capacitor-storage_refresh_token') {
        if (e.newValue === null && e.oldValue !== null) {
          console.error('🚨 TOKEN WAS DELETED FROM STORAGE!', {
            key: e.key,
            url: e.url,
            timestamp: new Date().toISOString()
          });
          Sentry.captureException(new Error(`Token deleted from storage: ${e.key}`));
        }
      }
    });
  }

  // Fetch system settings on app startup (required for all users)
  await fetchSystemSettingsAndContinue();
});

onUnmounted(() => {
  removeNetworkListener();
  removeLanguageSyncListener();
});
</script>
